<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import { useOrganizationStore } from '@/stores/organization';
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    organization: {
        type: Object,
        default: () => ({
            name: '',
            is_active: true,
            logo_url: null,
        })
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const form = ref({ ...props.organization });
form.value.logo = null;

const logoPreview = ref(props.organization.logo_url || null);

const toast = useToast();

const organizationStore = useOrganizationStore();
const { storeOrganization, updateOrganization } = organizationStore;

const onLogoChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
        form.value.logo = file;
        logoPreview.value = URL.createObjectURL(file);
    }
};

const save = async () => {
    try {
        const action = props.mode === 'Create' ? storeOrganization : updateOrganization;
        const toastMessage = props.mode === 'Create'
            ? trans('actions.create.success', { type: trans('label.organization') })
            : trans('actions.update.success', { type: trans('label.organization') });

        await action({
            ...form.value,
            hash: props.organization.hash,
        });

        toast.success(toastMessage);
        emit('close-modal');
    } catch (error) {
        toast.error(trans('actions.unexpected_error'));
    }
};
</script>
<template>
<ModalWrapper>
    <template #header>{{ mode }} {{ $t('label.organization') }}</template>
    <template #subheader>{{ $t('label.create_or_edit', { what: mode === 'Create' ? $t('label.organization') : form.name }) }}</template>

    <form @submit.prevent="save">
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3">
            <StyledInput
                v-model="form.name"
                name="name"
                :placeholder="$t('label.name')"
                :label="$t('label.name')" />

            <StyledRoundSwitch
                v-model="form.is_active"
                :label="$t('label.active')"
                inset
                name="is_active" />

            <div class="md:col-span-2 mb-2">
                <div class="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-lg border border-dashed border-gray-300">
                    <div v-if="logoPreview" class="flex-shrink-0">
                        <img :src="logoPreview" class="w-24 h-24 object-contain border rounded-md">
                    </div>
                    <StyledFileSelector
                        name="logo"
                        :label="logoPreview ? $t('label.replace_logo') : $t('label.upload_logo')"
                        accept="image/*"
                        class="w-60"
                        @change="onLogoChange" />
                </div>
            </div>
        </div>
    </form>

    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
